import React, { useState } from 'react';
import {
    TextField,
    Paper,
    List,
    ListItem,
    ListItemText
} from '@material-ui/core';

/**
 * SearchableSelect - A searchable select component with optional custom input support
 * 
 * @param {string} value - The current selected value
 * @param {function} onChange - Callback function when value changes
 * @param {array} options - Array of option objects
 * @param {string} label - Label for the input field
 * @param {boolean} disabled - Whether the input is disabled
 * @param {boolean} error - Whether to show error state
 * @param {string} helperText - Helper text to display
 * @param {function} getOptionLabel - Function to get the label from an option (default: option.name || option)
 * @param {function} getOptionValue - Function to get the value from an option (default: option.id || option)
 * @param {string} placeholder - Placeholder text when searching (default: "Search...")
 * @param {boolean} allowCustom - Whether to allow custom input values (default: true)
 */
const SearchableSelect = ({ 
    value, 
    onChange, 
    options, 
    label, 
    disabled, 
    error, 
    helperText,
    getOptionLabel = (option) => option.name || option,
    getOptionValue = (option) => option.id || option,
    placeholder = "Search...",
    allowCustom = true  // Whether to allow custom input
}) => {
    const [searchText, setSearchText] = useState('');
    const [open, setOpen] = useState(false);
    const inputRef = React.useRef(null);

    // Filter options based on search text
    const filteredOptions = options.filter(option => {
        const label = getOptionLabel(option);
        return label.toLowerCase().includes(searchText.toLowerCase());
    });

    // Get display value for current selection
    const selectedOption = value ? options.find(option => getOptionValue(option) === value) : null;
    // If custom input is not allowed and value is not in options, display empty
    const displayValue = selectedOption ? getOptionLabel(selectedOption) : (allowCustom ? (value || '') : '');

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        setSearchText(inputValue);
        setOpen(true);
        
        if (allowCustom) {
            // Allow custom input: check if input exactly matches an option's label
            const exactMatch = options.find(option => {
                const optionLabel = getOptionLabel(option);
                return optionLabel.toLowerCase() === inputValue.toLowerCase();
            });
            // If exact match, use option value; otherwise use user input (allow custom value)
            const newValue = exactMatch ? getOptionValue(exactMatch) : inputValue;
            onChange({ target: { value: newValue } });
        } else {
            // Don't allow custom input: only allow selection from options
            const exactMatch = options.find(option => {
                const optionLabel = getOptionLabel(option);
                return optionLabel.toLowerCase() === inputValue.toLowerCase();
            });
            // Only update value if exact match found
            if (exactMatch) {
                onChange({ target: { value: getOptionValue(exactMatch) } });
            }
        }
    };

    const handleSelect = (option) => {
        const optionValue = getOptionValue(option);
        onChange({ target: { value: optionValue } });
        setSearchText('');
        setOpen(false);
    };

    const handleFocus = () => {
        setOpen(true);
        setSearchText(value || '');
    };

    const handleBlur = (e) => {
        // Delay closing to allow option click to trigger
        setTimeout(() => {
            setOpen(false);
            if (allowCustom) {
                // If custom input is allowed: if current input is not in options, keep user's custom value
                if (searchText) {
                    const matchingOption = options.find(option => {
                        const optionValue = getOptionValue(option);
                        const optionLabel = getOptionLabel(option);
                        return optionValue === searchText || optionLabel === searchText;
                    });
                    // If no matching option, keep user's custom value
                    if (!matchingOption) {
                        onChange({ target: { value: searchText } });
                    }
                }
            } else {
                // If custom input is not allowed: if input is not in options, clear or restore original value
                if (searchText) {
                    const matchingOption = options.find(option => {
                        const optionLabel = getOptionLabel(option);
                        return optionLabel.toLowerCase() === searchText.toLowerCase();
                    });
                    // If no matching option, check if original value is valid
                    if (!matchingOption) {
                        const originalValueValid = value ? options.find(option => getOptionValue(option) === value) : false;
                        // If original value is valid, restore it; otherwise clear
                        onChange({ target: { value: originalValueValid ? value : '' } });
                    }
                }
            }
            setSearchText('');
        }, 200);
    };

    const handleMouseDown = (e) => {
        // Prevent blur event from triggering when clicking option
        e.preventDefault();
    };

    return (
        <div style={{ position: 'relative' }}>
            <TextField
                inputRef={inputRef}
                label={label}
                value={open ? searchText : displayValue}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={disabled}
                error={error}
                helperText={helperText}
                fullWidth
                variant="outlined"
                placeholder={open ? placeholder : undefined}
            />
            {open && (
                <Paper
                    style={{
                        position: 'absolute',
                        zIndex: 1300,
                        width: '100%',
                        maxHeight: 300,
                        overflow: 'auto',
                        marginTop: 4
                    }}
                >
                    {filteredOptions.length > 0 ? (
                        <List dense>
                            {filteredOptions.map((option) => {
                                const optionLabel = getOptionLabel(option);
                                const optionValue = getOptionValue(option);
                                return (
                                    <ListItem
                                        key={optionValue}
                                        button
                                        onClick={() => handleSelect(option)}
                                        onMouseDown={handleMouseDown}
                                        selected={value === optionValue}
                                    >
                                        <ListItemText primary={optionLabel} />
                                    </ListItem>
                                );
                            })}
                        </List>
                    ) : (
                        <ListItem>
                            <ListItemText primary={allowCustom ? "No matching options found. You can enter a custom value." : "No matching options found."} />
                        </ListItem>
                    )}
                </Paper>
            )}
        </div>
    );
};

export default SearchableSelect;

